'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { LogOut, Menu, Settings, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import type { LandingMessages } from '@/lib/marketing/landing-messages'
import { cn } from '@/lib/utils'

export type MarketingNavLink = { label: string; href: string }

export type MarketingAuth = {
  isLoggedIn: boolean
  /** `/in/<org>` or `/setup`; null when the account has no community yet. */
  dashboardUrl: string | null
}

const linkClass =
  'inline-flex min-h-en-control items-center rounded-en-md px-3 text-en-label font-semibold text-en-ink no-underline transition-colors duration-(--en-dur-feedback) hover:bg-en-surface-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus'

/**
 * The public site's top bar in Encuentro: the Alumnia lockup (blue on white,
 * 124 px, above the 120 px minimum), the page anchors, and one primary
 * ("Comenzar", ink). It sits on white and gains a line once the page
 * scrolls. On the phone the links move to a sheet from the right.
 *
 * Same destinations as the classic `Navbar`: tour, how it works, security,
 * pricing, sign in / get started, or the dashboard and account links when
 * signed in.
 */
export function MarketingNav({
  nav,
  homeHref,
  links,
  pricingHref,
  auth,
}: {
  nav: LandingMessages['nav']
  /** The locale's home (`/`, `/es`, `/fr`). */
  homeHref: string
  /** In-page anchors of the home (tour, how it works, security). */
  links: MarketingNavLink[]
  pricingHref: string
  auth: MarketingAuth
}) {
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const signOut = async () => {
    const { performSignOut } = await import('@/lib/utils/sign-out')
    await performSignOut()
  }

  const allLinks = [...links, { label: nav.pricing, href: pricingHref }]
  const accountHref = auth.dashboardUrl ?? '/settings'
  const accountLabel = auth.dashboardUrl ? nav.dashboard : nav.myAccount

  return (
    <header
      data-slot="marketing-nav"
      className={cn(
        'sticky top-0 z-40 border-b bg-en-bg transition-colors duration-(--en-dur-transition)',
        scrolled ? 'border-en-line' : 'border-transparent'
      )}
    >
      <nav
        aria-label={nav.menu}
        className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between gap-4 px-4 md:px-6"
      >
        <Link
          href={homeHref}
          className="inline-flex min-h-en-control items-center rounded-en-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
        >
          <Image
            src="/brand/encuentro/lockup-horizontal-blue.svg"
            alt="Alumnia"
            width={124}
            height={32}
            priority
            unoptimized
            className="h-8 w-auto"
          />
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {allLinks.map((link) => (
            <Link key={link.href} href={link.href} className={linkClass}>
              {link.label}
            </Link>
          ))}
          {auth.isLoggedIn ? (
            <div className="ml-2 flex items-center gap-2">
              <Button look="encuentro" variant="primary" asChild>
                <Link href={accountHref}>{accountLabel}</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button look="encuentro" variant="ghost" iconOnly aria-label={nav.myAccount}>
                    <User aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent look="encuentro" align="end">
                  <DropdownMenuItem asChild>
                    <Link href="/profile">
                      <User aria-hidden="true" />
                      {nav.profile}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/settings">
                      <Settings aria-hidden="true" />
                      {nav.settings}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={signOut}>
                    <LogOut aria-hidden="true" />
                    {nav.logOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="ml-2 flex items-center gap-2">
              <Link href="/login" className={linkClass}>
                {nav.signIn}
              </Link>
              <Button look="encuentro" variant="primary" asChild>
                <Link href="/start">{nav.getStarted}</Link>
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          {auth.isLoggedIn ? null : (
            <Button look="encuentro" variant="primary" size="sm" asChild>
              <Link href="/start">{nav.getStarted}</Link>
            </Button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button look="encuentro" variant="ghost" iconOnly aria-label={nav.openMenu}>
                <Menu aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              look="encuentro"
              closeLabel={nav.closeMenu}
              aria-describedby={undefined}
              className="flex w-full flex-col gap-0 p-0 sm:max-w-sm"
            >
              <SheetTitle className="sr-only">{nav.menu}</SheetTitle>
              <div className="flex min-h-16 items-center px-4">
                <Image
                  src="/brand/encuentro/lockup-horizontal-blue.svg"
                  alt=""
                  width={124}
                  height={32}
                  unoptimized
                  className="h-8 w-auto"
                />
              </div>
              <div className="grid min-h-0 flex-1 content-start gap-1 overflow-y-auto px-2 py-4">
                {allLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className={cn(linkClass, 'min-h-12 text-en-body')}
                  >
                    {link.label}
                  </Link>
                ))}
                {auth.isLoggedIn ? (
                  <>
                    <Link
                      href="/profile"
                      onClick={() => setOpen(false)}
                      className={cn(linkClass, 'min-h-12 gap-3 text-en-body')}
                    >
                      <User aria-hidden="true" className="size-5" />
                      {nav.profile}
                    </Link>
                    <Link
                      href="/settings"
                      onClick={() => setOpen(false)}
                      className={cn(linkClass, 'min-h-12 gap-3 text-en-body')}
                    >
                      <Settings aria-hidden="true" className="size-5" />
                      {nav.settings}
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        void signOut()
                      }}
                      className={cn(linkClass, 'min-h-12 w-full cursor-pointer gap-3 text-en-body')}
                    >
                      <LogOut aria-hidden="true" className="size-5" />
                      {nav.logOut}
                    </button>
                  </>
                ) : (
                  <Link
                    href="/login"
                    onClick={() => setOpen(false)}
                    className={cn(linkClass, 'min-h-12 text-en-body')}
                  >
                    {nav.signIn}
                  </Link>
                )}
              </div>
              <div className="border-t border-en-line p-4 pb-safe">
                <Button look="encuentro" variant="primary" block asChild>
                  <Link
                    href={auth.isLoggedIn ? accountHref : '/start'}
                    onClick={() => setOpen(false)}
                  >
                    {auth.isLoggedIn
                      ? auth.dashboardUrl
                        ? nav.goToDashboard
                        : nav.myAccount
                      : nav.getStarted}
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  )
}
